import { ITEMS, type ItemId } from '@/data/items';
import type { GroundItems } from '@/world/groundItems';
import type { Inventory } from '@/world/inventory';

/**
 * Picking things up off the floor.
 *
 * A rule with an edge worth naming: a full backpack must leave the pile where
 * it is rather than eating it. That is the whole reason this is not two lines
 * inline in the frame loop — "walk over a stack you have no room for" is
 * exactly the case nobody tries by hand.
 */

export type PickupOutcome =
  | { readonly kind: 'none' }
  | { readonly kind: 'picked'; readonly id: ItemId; readonly count: number; readonly name: string }
  | { readonly kind: 'full' };

export function pickUpAt(
  ground: GroundItems,
  inventory: Inventory,
  areaId: string,
  tileX: number,
  tileY: number,
): PickupOutcome {
  const pile = ground.at(areaId, tileX, tileY);
  if (!pile) return { kind: 'none' };

  // Asked before taking: a pile removed and then found not to fit would be
  // gone, which is the one thing dropping was built to never do.
  if (!inventory.hasRoomFor(pile.id, pile.count)) return { kind: 'full' };

  ground.take(areaId, pile.x, pile.y);
  inventory.add(pile.id, pile.count);

  return { kind: 'picked', id: pile.id, count: pile.count, name: ITEMS[pile.id].name };
}

/** What to tell the player, or null when nothing worth saying happened. */
export function describePickup(outcome: PickupOutcome): string | null {
  switch (outcome.kind) {
    case 'picked':
      return outcome.count > 1
        ? `Picked up ${outcome.count} ${outcome.name}`
        : `Picked up ${outcome.name}`;
    case 'full':
      return 'Your backpack is full';
    case 'none':
      return null;
  }
}
